// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the bootstrap. Owns the screens and the room, and no game logic whatsoever.
//
// The room is joined ONCE and held until the player goes back to the menu. Every round after the
// first — every "play again" — happens inside it, versioned by rematch.ts. Leaving and re-joining to
// "reset" hands back the room object Trystero is still tearing down, and both players then sit in
// the right room code, alone, for ever.

import '@ben-gy/game-engine/mobile.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  mintCode,
  normalizeRoomCode,
  setRoomInUrl,
  type Lobby,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { TIERS, TIER_IDS, tierOf } from './bot';
import { DEFAULT_MODE, MODES, MODE_IDS, modeOf } from './modes';
import { runCountdown } from './countdown';
import { Session } from './session';
import { Table, type Selection } from './table';
import { summarise } from './view';
import { HOW_HTML, aboutHtml, menuHtml, openPanel, summaryHtml } from './ui';
import './styles/main.css';
import type { Ev } from './game';

const SLUG = 'sparklight';
const CREW_NAMES = ['Sorrel', 'Pell', 'Nim', 'Odile'];

const app = document.getElementById('app')!;
const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  // Every cue is a rising or falling glide chosen so the table can be read with your eyes shut:
  // raising climbs, dousing falls, a completed plume soars, and hazing is the only ugly noise.
  patches: {
    tap: { type: 'square', freq: [520, 520], dur: 0.03, gain: 0.14 },
    raise: { type: 'sine', freq: [520, 880], dur: 0.16, gain: 0.24 },
    douse: { type: 'triangle', freq: [340, 190], dur: 0.11, gain: 0.18 },
    point: { type: 'sine', freq: [760, 1010], dur: 0.09, gain: 0.2 },
    haze: { type: 'sawtooth', freq: [260, 70], dur: 0.32, gain: 0.3, noise: true },
    plume: { type: 'triangle', freq: [660, 1560], dur: 0.36, gain: 0.28 },
    tick: { type: 'triangle', freq: [660, 660], dur: 0.08, gain: 0.2 },
    go: { type: 'triangle', freq: [880, 1320], dur: 0.22, gain: 0.26 },
    over: { type: 'triangle', freq: [520, 300], dur: 0.5, gain: 0.24 },
  },
});
const play = (n: string): void => {
  try {
    sfx.play(n);
  } catch {
    /* audio is decoration; never let it take the game down */
  }
};

interface Settings {
  mode: string;
  tier: string;
  crew: number;
}
const settings: Settings = {
  mode: modeOf(store.get<string>('mode', DEFAULT_MODE)).id,
  tier: tierOf(store.get<string>('tier', 'sharp')).id,
  crew: Math.min(5, Math.max(2, store.get<number>('crew', 3) ?? 3)),
};

let net: Net | null = null;
let rounds: Rounds<RoundOpts> | null = null;
let lobby: Lobby | null = null;
let session: Session | null = null;
let table: Table | null = null;
let roomCode = '';
let iMintedIt = false;
let sel: Selection = { kind: 'none' };
let beat: ReturnType<typeof setInterval> | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;
let countdown: { cancel: () => void } | null = null;
let deepLinkUsed = false;

interface RoundOpts {
  mode: string;
}

// ── screens ───────────────────────────────────────────────────────────────────────────────────────

function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function clearScreen(): void {
  countdown?.cancel();
  countdown = null;
  table?.destroy();
  table = null;
  if (ticker) clearInterval(ticker);
  ticker = null;
  app.replaceChildren();
}

function showMenu(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = menuHtml(settings.mode, settings.tier, settings.crew, sfx.muted());

  app.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      settings.mode = b.dataset.mode!;
      store.set('mode', settings.mode);
      showMenu();
    }),
  );
  app.querySelectorAll<HTMLButtonElement>('[data-crew]').forEach((b) =>
    b.addEventListener('click', () => {
      settings.crew = Number(b.dataset.crew);
      store.set('crew', settings.crew);
      showMenu();
    }),
  );
  app.querySelectorAll<HTMLButtonElement>('[data-tier]').forEach((b) =>
    b.addEventListener('click', () => {
      settings.tier = b.dataset.tier!;
      store.set('tier', settings.tier);
      showMenu();
    }),
  );

  app.querySelector('#playSolo')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    startSolo();
  });
  app.querySelector('#playFriends')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    showRoomEntry();
  });
  app.querySelector('#howBtn')!.addEventListener('click', () => openPanel(app, HOW_HTML));
  app.querySelector('#aboutBtn')!.addEventListener('click', () => openPanel(app, aboutHtml()));
  const mute = app.querySelector<HTMLButtonElement>('#muteBtn')!;
  mute.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    mute.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', String(sfx.muted()));
  });

  if (!store.get<boolean>('seenHow', false)) {
    store.set('seenHow', true);
    openPanel(app, HOW_HTML);
  }
}

// ── solo ──────────────────────────────────────────────────────────────────────────────────────────

function startSolo(): void {
  clearScreen();
  const players = settings.crew;
  const names = ['You', ...CREW_NAMES].slice(0, players);
  const bots = new Set<number>();
  for (let s = 1; s < players; s++) bots.add(s);
  buildSession({
    players,
    names,
    mySeat: 0,
    bots,
    isHost: true,
    peerOfSeat: Array.from({ length: players }, () => ''),
    seed: newSeed(),
    round: 1,
    mode: settings.mode,
  });
}

// ── the room ──────────────────────────────────────────────────────────────────────────────────────

function showRoomEntry(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  createRoomEntry({
    container: host,
    title: 'Play with friends',
    subtitle: 'Start a room and send the link — or scan the code, or type it in.',
    onSubmit: (code, created) => {
      roomCode = normalizeRoomCode(code);
      iMintedIt = created;
      enterRoom();
    },
    onCancel: () => showMenu(),
  });
}

function enterRoom(): void {
  clearScreen();
  setRoomInUrl(roomCode);
  net = createNet(
    { appId: roomAppId(SLUG), roomId: roomCode, claimHost: iMintedIt },
    {
      onPeers: () => {
        lobby?.repaint();
        session?.tick();
      },
      onPeerLeave: (id: PeerId) => {
        session?.peerLeft(id);
        lobby?.repaint();
      },
      onHostChange: (_id, isSelf) => {
        if (isSelf) session?.becomeHost();
        lobby?.repaint();
        repaintTable();
      },
    },
  );

  const wire = net.channel<unknown>('spk', (m, from) => session?.receive(m as never, from));
  const priv = net.channel<unknown>('spkeye', (m, from) => session?.receive(m as never, from));

  rounds = createRounds<RoundOpts>({
    net,
    playerName: store.get<string>('name', '') || `Player ${Math.floor(Math.random() * 90 + 10)}`,
    minPlayers: 2,
    roundOpts: () => ({ mode: settings.mode }),
    onRound: (info) => {
      // The HOST's pick is what the room plays, frozen inside the start, so two peers can never be
      // on different decks. `modeOf` guards it: an unknown id off the wire falls back rather than
      // reaching the dealer as undefined.
      const mode = modeOf(info.opts?.mode).id;
      if (!info.seated) {
        showSpectator();
        return;
      }
      const players = info.players.length;
      const mySeat = info.players.findIndex((p) => p.id === net!.selfId);
      startRound({
        players,
        names: info.players.map((p) => p.name),
        mySeat,
        bots: new Set<number>(),
        isHost: info.isHost,
        peerOfSeat: info.players.map((p) => p.id),
        seed: info.seed,
        round: info.round,
        mode,
      });
    },
    onChange: () => lobby?.repaint(),
  });

  sendWire = (m, to) => (to === undefined ? wire(m) : priv(m, to as PeerId));
  showLobby();
}

let sendWire: (m: unknown, to?: string | string[]) => void = () => {};

function showLobby(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  lobby = createLobby({
    container: host,
    net: net!,
    rounds: rounds!,
    roomCode,
    minPlayers: 2,
    maxPlayers: 5,
    modeSlot: () =>
      `<div class="modeslot"><span class="modeslot-label">Night</span>
       <div class="segmented small">${MODE_IDS.map((id) => {
         const chosen = rounds!.state().isHost ? settings.mode : (rounds!.state().hostOpts?.mode ?? settings.mode);
         return `<button type="button" class="seg${id === chosen ? ' on' : ''}" data-lmode="${id}"${
           rounds!.state().isHost ? '' : ' disabled'
         }>${MODES[id].name}</button>`;
       }).join('')}</div>
       <p class="fine">${
         rounds!.state().isHost ? MODES[settings.mode].blurb : "The host picks the night."
       }</p></div>`,
    onModeMount: () => {
      host.querySelectorAll<HTMLButtonElement>('[data-lmode]').forEach((b) =>
        b.addEventListener('click', () => {
          settings.mode = b.dataset.lmode!;
          store.set('mode', settings.mode);
          lobby?.repaint();
        }),
      );
    },
    onCancel: () => leaveRoom(),
  });
  app.insertAdjacentHTML('beforeend', `<footer class="site-footer">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a></footer>`);
}

function showSpectator(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = `<div class="main-content"><h2>A night is already under way</h2>
    <p>You are in the next one. Hang on — it will start on its own.</p></div>`;
}

/**
 * Leaving is the ONLY place the room is torn down, and it clears `?room=` on the way out. Leave it
 * in the URL and a reload — or reopening from a home-screen icon — silently drags the player back
 * into a room they left, with no way to start a new one.
 */
function leaveRoom(): void {
  if (beat) clearInterval(beat);
  beat = null;
  session?.destroy();
  session = null;
  rounds?.destroy();
  rounds = null;
  lobby?.destroy();
  lobby = null;
  net?.leave();
  net = null;
  clearRoomInUrl();
  deepLinkUsed = true;
  showMenu();
}

// ── a round ───────────────────────────────────────────────────────────────────────────────────────

interface StartArgs {
  players: number;
  names: string[];
  mySeat: number;
  bots: Set<number>;
  isHost: boolean;
  peerOfSeat: string[];
  seed: number;
  round: number;
  mode: string;
}

function startRound(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  const holder = document.createElement('div');
  holder.className = 'countwrap';
  app.append(holder);
  countdown = runCountdown({
    container: holder,
    sfx: play,
    onDone: () => {
      holder.remove();
      buildSession(args);
    },
  });
}

function buildSession(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  const mode = modeOf(args.mode);
  table = new Table({
    submit: (a) => {
      session?.submit(a);
      sel = { kind: 'none' };
      repaintTable();
    },
    onSelect: (s) => {
      sel = s;
      repaintTable();
    },
    sfx: play,
    onQuit: () => {
      // Back to the lobby, never out of the room — and `rounds.finish()` FIRST, because the engine
      // lobby paints nothing at all while the round is still 'playing' and this seat is seated.
      if (net) {
        rounds?.finish();
        showLobby();
      } else showMenu();
    },
  });
  app.append(table.root);

  session = new Session({
    mode,
    round: args.round,
    players: args.players,
    mySeat: args.mySeat,
    bots: args.bots,
    tier: tierOf(settings.tier),
    isHost: args.isHost,
    names: args.names,
    peerOfSeat: [...args.peerOfSeat],
    seed: args.seed,
    deps: {
      send: (m, to) => sendWire(m, to),
      onChange: () => repaintTable(),
      onEvents: (evs) => narrate(evs, args.names),
      onEnd: () => showResults(),
    },
  });

  if (beat) clearInterval(beat);
  // setInterval, never rAF: a backgrounded tab must keep covering an absent seat and keep the
  // heartbeat alive, or the crew freezes the moment somebody checks a message.
  beat = setInterval(() => session?.beat(), 2000);
  ticker = setInterval(() => session?.tick(), 1000);
  repaintTable();
}

function repaintTable(): void {
  const s = session;
  if (!table || !s || !s.g) return;
  table.render({
    g: s.g,
    you: s.mySeat,
    names: s.names,
    away: s.away,
    bots: s.bots,
    sel,
    dealerSeat: s.dealerSeat,
    pending: false,
    sixth: s.mode.sixth ? (s.mode.sixth.singleton ? 'onyx' : 'prism') : null,
  });
  if (s.mismatch) table.say('Your crew is on a different build — reload the page.');
  else if (s.rebuilding) table.say('Picking up the deck…');
}

function narrate(evs: Ev[], names: string[]): void {
  for (const e of evs) {
    if (e.k === 'raise') {
      play(e.ok ? (e.height === 5 ? 'plume' : 'raise') : 'haze');
      table?.say(
        e.ok
          ? `${names[e.seat] ?? 'They'} raised it to ${e.height}.`
          : `${names[e.seat] ?? 'They'} raised the wrong card. The sky hazes.`,
      );
      if (!e.ok) table?.root.classList.add('shake');
      setTimeout(() => table?.root.classList.remove('shake'), 320);
    } else if (e.k === 'douse') {
      play('douse');
      table?.say(`${names[e.seat] ?? 'They'} doused a card. A spark comes back.`);
    } else if (e.k === 'point') {
      play('point');
      const what = e.kind === 1 ? `the ${e.value}s` : 'a colour';
      table?.say(`${names[e.seat] ?? 'They'} pointed out ${what} to ${names[e.target] ?? 'someone'}.`);
    } else if (e.k === 'end') play('over');
  }
}

// ── the summary ───────────────────────────────────────────────────────────────────────────────────

function showResults(): void {
  const s = session;
  if (!s || !s.g) return;
  setPlaying(false);
  const sum = summarise(s.g, s.names);
  clearScreen();
  app.innerHTML = summaryHtml(sum, s.mode.sixth ? (s.mode.sixth.singleton ? 'onyx' : 'prism') : null, s.mySeat);

  const again = app.querySelector<HTMLButtonElement>('#againBtn')!;
  const toLobby = app.querySelector<HTMLButtonElement>('#lobbyBtn')!;
  const wait = app.querySelector<HTMLElement>('#resWait')!;

  if (net && rounds) {
    rounds.finish();
    toLobby.hidden = false;
    toLobby.addEventListener('click', () => showLobby());
    again.addEventListener('click', () => {
      rounds!.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the crew…';
    });
    // A visible reason and a horizon. One player still reading the summary must not hold the room.
    const poll = setInterval(() => {
      const st = rounds?.state();
      if (!st) return;
      wait.hidden = false;
      wait.textContent =
        st.startsInMs !== null
          ? `Next night in ${Math.ceil(st.startsInMs / 1000)}s…`
          : `${st.votes.length} of ${st.present.length} ready.`;
    }, 400);
    ticker = poll;
  } else {
    again.addEventListener('click', () => {
      play('tap');
      startSolo();
    });
  }
  app.querySelector('#menuBtn')!.addEventListener('click', () => (net ? leaveRoom() : showMenu()));
  app
    .querySelector('.results-feedback')!
    .addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
}

// ── boot ──────────────────────────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();
  // ONCE, before any mesh exists. Trystero builds one global connection pool from the first room's
  // config, so a later call leaves the initiating half of every pair STUN-only.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fails open — TURN is an upgrade, never a dependency */
  }

  // A `?room=` is honoured ONCE. After that, "play with friends" always offers create-or-join, so a
  // reload can never drag somebody back into a room they have left.
  const deep = new URL(location.href).searchParams.get('room');
  if (deep && !deepLinkUsed) {
    deepLinkUsed = true;
    roomCode = normalizeRoomCode(deep);
    iMintedIt = false;
    enterRoom();
    return;
  }
  showMenu();
}

window.addEventListener('beforeunload', () => net?.leave());
void boot();

// Exposed for the browser verification pass: drives a round synchronously in a tab where rAF and
// real timers are throttled. Never used by the game itself.
(window as unknown as Record<string, unknown>).__spark = {
  step: () => session?.tick(),
  state: () => session?.g,
  submit: (a: number) => session?.submit(a),
  mint: () => mintCode(),
  tiers: TIER_IDS.map((t) => TIERS[t].name),
};
